import type { PartyVitalsRecord } from "odyssey-shared";
import type { PartyMember } from "./members.ts";
import { NO_VITALS, memberVitalsOf, recordMatches, sameMember, type MemberVitals } from "./vitals.ts";

/**
 * What the page holds of the party's vitals: the members the last `partyVitals` frame named and the player's own,
 * which the frame never carries. `forSeat` returns the same map while neither has changed, so a snapshot built from
 * it compares by identity.
 */
export class VitalsBook {
  private others: ReadonlyMap<number, MemberVitals> = NO_VITALS;
  private own: MemberVitals | null = null;
  private joined: { readonly others: ReadonlyMap<number, MemberVitals>; readonly own: MemberVitals | null; readonly place: number; readonly map: ReadonlyMap<number, MemberVitals> } | null = null;

  /**
   * Takes a frame.
   *
   * @param record The decoded frame.
   * @returns True when the members' vitals differ from the last frame's.
   */
  public receive(record: Readonly<PartyVitalsRecord>): boolean {
    if (recordMatches(record, this.others)) {
      return false;
    }
    this.others = memberVitalsOf(record);
    return true;
  }

  /**
   * Sets the player's own vitals.
   *
   * @param own The vitals, or null before the first `vitals`.
   * @returns True when they differ from the last.
   */
  public setOwn(own: MemberVitals | null): boolean {
    if (own === this.own || (own !== null && this.own !== null && sameMember(own, this.own))) {
      return false;
    }
    this.own = own;
    return true;
  }

  /**
   * Forgets the members a frame named, as a connection's end does.
   */
  public clear(): void {
    this.others = NO_VITALS;
  }

  /**
   * Drops the vitals of places a roster no longer seats, so a member who takes a place next is not drawn with the last
   * one's health; a roster under two seats drops them all.
   *
   * @param members The roster.
   */
  public keepSeated(members: readonly PartyMember[]): void {
    if (members.length < 2) {
      this.others = NO_VITALS;
      return;
    }
    const kept = [...this.others].filter(([place]) => members.some((member) => member.place === place));
    if (kept.length !== this.others.size) {
      this.others = kept.length === 0 ? NO_VITALS : new Map(kept);
    }
  }

  /**
   * The vitals the frames draw: the last frame's members and, on the player's own place, the player's.
   *
   * @param self The player's seat, or null.
   * @returns The map; the same one while its inputs are unchanged.
   */
  public forSeat(self: PartyMember | null): ReadonlyMap<number, MemberVitals> {
    const place = self === null || this.own === null ? -1 : self.place;
    const held = this.joined;
    if (held !== null && held.others === this.others && held.own === this.own && held.place === place) {
      return held.map;
    }
    const map = place < 0 ? this.others : new Map([...this.others, [place, this.own as MemberVitals]]);
    this.joined = { others: this.others, own: this.own, place, map };
    return map;
  }
}
